import { getSafeAreaPaddings, getTheme } from "@clipboard-health/ui-theme";
import { InputChangeEventDetail } from "@ionic/core";
import {
  IonButton,
  IonContent,
  IonFooter,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
} from "@ionic/react";
import { ThemeProvider } from "@mui/material";
import { useToast } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { PageWrapper } from "@src/appV2/redesign/components/PageWrapper";
import { Worker } from "@src/appV2/Worker/api/types";
import { useUpdateWorkerCache } from "@src/appV2/Worker/useUpdateWorkerCache";
import { getAddressByGeoCode } from "@src/lib/deprecatedCode";
import { Location } from "@src/lib/interface";
import { pin } from "ionicons/icons";
import { FC, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import { AddressEditMap } from "./addressEditMap";
import { updateAddress } from "./api";
import { USER_EVENTS } from "../../constants/userEvents";
import { setAddressEditDetails, updateAddressLine1 } from "../store/accountSettings";
import { Store } from "../store/store.model";

const AddressEdit: FC = () => {
  const [locating, setLocating] = useState<boolean>(false);
  const { addressEditDetails } = useSelector((state: Store) => state.accountSettingsStore);
  const dispatch = useDispatch();
  const history = useHistory();
  const updateWorkerCache = useUpdateWorkerCache();
  const [isAddressUpdating, setIsAddressUpdating] = useState<boolean>(false);
  const { showErrorToast } = useToast();

  const updateMapAddress = async (markerLocation: Location): Promise<void> => {
    const geocodeAddress = await getAddressByGeoCode(markerLocation);
    const newAddress = {
      ...addressEditDetails?.address,
      ...geocodeAddress,
      line1: addressEditDetails?.addressLine1,
      subpremise: "",
    };
    dispatch(
      setAddressEditDetails({
        address: newAddress,
        location: markerLocation,
        searchLocation: newAddress.formatted,
        addressLine1: addressEditDetails?.addressLine1,
      })
    );

    setLocating(false);
  };

  const onLocationChange = (markerLocation: Location): void => {
    setLocating(true);
    updateMapAddress(markerLocation);
  };

  const onAddressLineOneChange = ({ detail }: CustomEvent<InputChangeEventDetail>) => {
    dispatch(updateAddressLine1(detail.value as string));
  };

  const onUpdateAddress = async () => {
    setIsAddressUpdating(true);
    const updatedAddress = await updateAddress({
      address: {
        ...addressEditDetails?.address,
        line1: addressEditDetails?.addressLine1 ?? "",
        subpremise: addressEditDetails?.address?.subpremise ?? "",
      },
      location: addressEditDetails?.location as Location,
    });
    setIsAddressUpdating(false);

    if (!updatedAddress) {
      showErrorToast("Unable to update your address at this time. Please try again.");
      return;
    }

    const { geoLocation, address, status } = updatedAddress;

    if (status === 200) {
      logEvent(USER_EVENTS.UPDATED_ADDRESS);
    }
    updateWorkerCache({
      // I believe that updateAddress is just incorrectly typed, that's why these casts
      geoLocation: geoLocation as Worker["geoLocation"],
      address: address as unknown as Worker["address"],
    });
    history.go(-2);
  };

  const onEditSearchQuery = () => {
    history.goBack();
  };

  return (
    <ThemeProvider theme={getTheme()}>
      <PageWrapper
        sx={(theme) => ({
          ...getSafeAreaPaddings({
            top: true,
            bottom: true,
          }),
          background: "#fff",
        })}
      >
        <IonContent>
          <AddressEditMap location={addressEditDetails?.location} onChange={onLocationChange} />
        </IonContent>
        <IonFooter className="ion-padding">
          <IonItem>
            <IonLabel position="stacked" color="primary">
              <IonIcon icon={pin} /> {locating ? "Locating..." : "Address"}
            </IonLabel>
            <IonInput readonly={true} value={addressEditDetails?.searchLocation} />
            <IonButton size="small" slot="end" fill="outline" onClick={onEditSearchQuery}>
              Change
            </IonButton>
          </IonItem>
          <IonItem>
            <IonLabel position="stacked">Apartment/Floor/Building (if applicable)</IonLabel>
            <IonInput
              debounce={1000}
              // disable if the user has already picked a subpremise using autocomplete
              disabled={addressEditDetails?.address?.subpremise ? true : false}
              clearInput={true}
              placeholder="Apartment, e.g. “Unit 2”"
              autofocus={true}
              value={addressEditDetails?.address?.subpremise || addressEditDetails?.addressLine1}
              onIonChange={onAddressLineOneChange}
            />
          </IonItem>

          <LoadingButton
            isLoading={isAddressUpdating}
            onClick={onUpdateAddress}
            fullWidth
            sx={{
              marginTop: 1,
            }}
            size="small"
          >
            Update Address
          </LoadingButton>
        </IonFooter>
      </PageWrapper>
    </ThemeProvider>
  );
};

export { AddressEdit };
